<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';

import { eqsApi } from '@/api/eqs';
import { confirmAction } from '@/services/confirm';
import { notify } from '@/services/notice';
import { errorMessage } from '@/utils/format';

/**
 * BOM 发布与版本。
 *
 * 替掉两张 Excel 里手写的「更改履历表」——X04C 的 EBOM 记到 A12、MBOM 记到 A2，
 * 改了什么全靠人自觉填。这里草稿随便改，发布时冻结一版，
 * **改了什么是跟上一版逐行比出来的**，不是写出来的。
 */
const props = defineProps<{
  projectId: string | number;
  bomType: 'ebom' | 'mbom';
  canWrite: boolean;
}>();

interface Release {
  id: number; revision: string; summary: string | null;
  itemCount: number; releasedByName: string | null; releasedAt: string;
}
interface DiffLine {
  kind: 'added' | 'removed' | 'changed';
  partNumber: string; partName: string; parentPartNumber: string | null;
  changes: Array<{ field: string; before: string; after: string }>;
}
/**
 * 发布后的通知战况（0827）。发布即通知项目团队，但**通知不到的人必须点名**——
 * 只说「已通知 8 人」，团队里没账号、没邮箱、白名单外的那几个人就被算成通知到了。
 */
interface NotifyResult {
  inapp: number;
  mail: { mode: 'off' | 'whitelist' | 'on'; sent: number; skippedNames: string[] };
  withoutAccount: number;
}
/** 移交量产后的项目（1030）：发布改发量产接收名单，等他们确认 */
interface SyncResult {
  receivers: number;
  alreadySent: number;
  mail: { mode: 'off' | 'whitelist' | 'on'; sent: number; skippedNames: string[] };
}

const open = ref(false);
const loading = ref(false);
const releases = ref<Release[]>([]);
const nextRevision = ref('A0');
const draftCount = ref(0);
const baseRevision = ref<string | null>(null);
const diff = ref<DiffLine[]>([]);
const handedOver = ref(false);
const revisionInput = ref('');
const summaryInput = ref('');
const saving = ref(false);

const KIND_LABEL: Record<string, string> = { added: '新增', removed: '删除', changed: '修改' };

const pending = computed(() => diff.value.length);
const latest = computed(() => releases.value[0] ?? null);

async function load(): Promise<void> {
  loading.value = true;
  try {
    const payload = await eqsApi.bomReleases(props.projectId, props.bomType) as unknown as {
      releases: Release[]; nextRevision: string; draftCount: number;
      baseRevision: string | null; diff: DiffLine[]; handedOver?: boolean;
    };
    releases.value = payload.releases ?? [];
    nextRevision.value = payload.nextRevision ?? 'A0';
    draftCount.value = payload.draftCount ?? 0;
    baseRevision.value = payload.baseRevision ?? null;
    diff.value = payload.diff ?? [];
    handedOver.value = payload.handedOver === true;
    revisionInput.value = payload.nextRevision ?? 'A0';
  } catch (caught) {
    notify('error', '读取版本历史失败', errorMessage(caught));
  } finally {
    loading.value = false;
  }
}
onMounted(load);
watch(() => props.bomType, load);

/**
 * 通知战况写成一句人话。
 *
 * 三件事都得说，少一件就是把「没通知到」说成「通知到了」：
 * ① 站内发了几个人；② 邮件发了几封、闸门是什么状态；③ 谁没收到、为什么。
 * 后端没返回 notified（通知那一步炸了）时照实说发布成了但通知没成——
 * 静悄悄地当通知过了，下游就会以为大家都知道了。
 */
function notifyLine(result: NotifyResult | undefined): string {
  if (!result) return '通知没发出去（发布本身已完成，去日志看原因）。';
  const parts = [`已通知团队 ${result.inapp} 人（站内）`];
  if (result.mail.sent) parts.push(`邮件 ${result.mail.sent} 封`);
  else if (result.mail.mode === 'off') parts.push('邮件通道关着，没发');
  else parts.push('邮件一封没发出去');
  const missed: string[] = [];
  if (result.withoutAccount) missed.push(`${result.withoutAccount} 人没有系统账号`);
  if (result.mail.skippedNames.length) missed.push(`邮件跳过 ${result.mail.skippedNames.join('、')}`);
  if (missed.length) parts.push(`收不到的：${missed.join('；')}`);
  return `${parts.join('，')}。`;
}

function syncLine(result: SyncResult | undefined): string {
  if (!result) return '没同步出去（发布本身已完成，可以到量产 BOM 里补发）。';
  const parts = [`已同步给量产接收人 ${result.receivers} 人，等他们确认`];
  if (result.mail.sent) parts.push(`邮件 ${result.mail.sent} 封`);
  else if (result.mail.mode === 'off') parts.push('邮件通道关着，没发');
  if (result.mail.skippedNames.length) parts.push(`邮件跳过 ${result.mail.skippedNames.join('、')}`);
  return `${parts.join('，')}。`;
}

async function release(): Promise<void> {
  const revision = revisionInput.value.trim() || nextRevision.value;
  if (!await confirmAction({
    title: `发布 ${revision}？`,
    message: (latest.value
      ? `会冻结当前 ${draftCount.value} 行，并记下与 ${latest.value.revision} 的 ${pending.value} 处差异。发布过的版本不能覆盖。`
      : `会冻结当前 ${draftCount.value} 行作为第一版。发布过的版本不能覆盖。`)
      // 发布会给一群人发通知，点之前就得知道，不能发完才发现
      + (latest.value && !pending.value
        // 0926：内容没变就不通知——点之前就说清楚，免得人以为大家都收到了
        ? `内容和 ${latest.value.revision} 完全相同，发布只冻结一个新版本号，`
          + `${handedOver.value ? '不会同步给量产接收人' : '不会通知项目团队'}。`
        : handedOver.value
          // 移交后：发的是量产 BOM 的接收名单，不是项目团队
          ? '项目已移交量产，发布后会同步给量产接收人并等他们确认（名单在模块中心「量产 BOM」里）。'
          : '发布后会立即通知项目团队（站内通知，邮件按开关）。'),
    confirmLabel: '发布',
  })) return;

  saving.value = true;
  try {
    const result = await eqsApi.releaseBom(props.projectId, {
      type: props.bomType, revision, summary: summaryInput.value || null,
    }) as unknown as {
      release: Release; diff: DiffLine[]; notified?: NotifyResult; notifySkipped?: string;
      synced?: SyncResult;
    };
    notify('success', `已发布 ${result.release.revision}`,
      [
        result.release.summary ?? `冻结 ${result.release.itemCount} 行`,
        // 内容没变是故意不通知（后端给了原因），不能落到「通知没发出去」那句去
        result.notifySkipped
          ?? (handedOver.value ? syncLine(result.synced) : notifyLine(result.notified)),
      ].filter(Boolean).join('　'));
    summaryInput.value = '';
    await load();
  } catch (caught) {
    notify('error', '发布失败', errorMessage(caught));
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <section class="release-bar">
    <button type="button" class="release-toggle" @click="open = !open">
      <b>{{ latest ? `当前版本 ${latest.revision}` : '还没发布过' }}</b>
      <span v-if="pending" class="release-pending">较上一版有 {{ pending }} 处改动</span>
      <span v-else-if="latest" class="release-clean">与上一版一致</span>
      <em>{{ open ? '收起' : '版本与改动' }}</em>
    </button>

    <div v-if="open" class="release-body">
      <p v-if="loading" class="release-empty">读取中…</p>
      <template v-else>
        <div v-if="canWrite" class="release-form">
          <label>
            <span>版本号</span>
            <input v-model="revisionInput" type="text" :placeholder="nextRevision">
          </label>
          <label class="release-summary">
            <span>改了什么（留空自动按差异生成）</span>
            <input v-model="summaryInput" type="text" placeholder="如：侧风口叶片共用，数量改为 4">
          </label>
          <button class="button button-primary" type="button" :disabled="saving || !draftCount" @click="release">
            {{ saving ? '发布中…' : `发布 ${revisionInput || nextRevision}` }}
          </button>
        </div>

        <div v-if="pending" class="release-diff">
          <b>与 {{ baseRevision }} 相比：</b>
          <ul>
            <li v-for="(line, index) in diff.slice(0, 30)" :key="index" :class="line.kind">
              <span class="diff-kind">{{ KIND_LABEL[line.kind] }}</span>
              <b>{{ line.partNumber }}</b> {{ line.partName }}
              <template v-if="line.parentPartNumber">（在 {{ line.parentPartNumber }} 下）</template>
              <em v-for="change in line.changes" :key="change.field">
                {{ change.field }}：{{ change.before || '空' }} → {{ change.after || '空' }}
              </em>
            </li>
          </ul>
          <p v-if="diff.length > 30" class="release-empty">还有 {{ diff.length - 30 }} 处，发布后可在历史里查。</p>
        </div>
        <p v-else-if="latest" class="release-empty">当前内容和 {{ latest.revision }} 完全一致，没有要发布的改动。</p>

        <table v-if="releases.length" class="data-table release-history">
          <thead><tr><th>版本</th><th>改了什么</th><th>行数</th><th>发布人</th><th>时间</th></tr></thead>
          <tbody>
            <tr v-for="item in releases" :key="item.id">
              <td><b>{{ item.revision }}</b></td>
              <td>{{ item.summary || '—' }}</td>
              <td>{{ item.itemCount }}</td>
              <td>{{ item.releasedByName || '—' }}</td>
              <td>{{ item.releasedAt.slice(0, 16).replace('T', ' ') }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="release-empty">
          还没有发布过。发布会冻结当前内容作为一版，之后的改动都会和它比对——
          这份履历不用再手工维护。
        </p>
      </template>
    </div>
  </section>
</template>

<style scoped>
.release-bar { margin-bottom: 12px; border: 1px solid var(--line); background: var(--white); }
.release-toggle {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 9px 13px; border: 0; background: var(--surface); cursor: pointer; text-align: left;
}
.release-toggle b { font: 800 13px var(--display); }
.release-pending { color: #8a5a00; font-size: 12px; font-weight: 800; }
.release-clean { color: var(--muted); font-size: 12px; }
.release-toggle em { margin-left: auto; color: var(--blue); font-style: normal; font-size: 12px; }

.release-body { padding: 12px 13px; }
.release-empty { margin: 0; padding: 6px 0; color: var(--muted); font-size: 12px; line-height: 1.8; }

.release-form { display: flex; align-items: flex-end; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
.release-form label { display: grid; gap: 4px; }
.release-form label > span { color: var(--muted); font-size: 11px; }
.release-form input { min-height: 34px; padding: 0 9px; border: 1px solid var(--line); border-radius: 4px; font-size: 12px; }
.release-summary { flex: 1 1 280px; }
.release-summary input { width: 100%; }

.release-diff { margin-bottom: 12px; font-size: 12px; }
.release-diff ul { max-height: 220px; margin: 6px 0 0; padding-left: 0; overflow: auto; list-style: none; display: grid; gap: 4px; }
.release-diff li { padding: 5px 8px; background: var(--paper); line-height: 1.7; }
.release-diff li.added { border-left: 3px solid #2f7d32; }
.release-diff li.removed { border-left: 3px solid var(--risk); }
.release-diff li.changed { border-left: 3px solid var(--blue); }
.diff-kind { display: inline-block; margin-right: 6px; font-weight: 800; }
.release-diff em { display: block; margin-left: 14px; color: var(--muted); font-style: normal; }

.release-history { width: 100%; font-size: 12px; }
.release-history th, .release-history td { padding: 6px 9px; border-bottom: 1px solid var(--line); text-align: left; }
.release-history th { color: var(--muted); background: #f3f5f8; }
</style>
